import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import Header from './components/common/Header'
import Footer from './components/common/Footer'
import ProtectedRoute from './components/common/Auth/ProtectedRoute'
import ScrollToTop from './components/common/ScrollToTop'
import NotFoundPage from './pages/NotFoundPage'
import TimeOffPage from './pages/Requestor/RequestsContainer'
import RequestStatusSummary from './pages/Requestor/RequestStatusSummary'
import ApproverContainer from './pages/Approver/ApproverContainer'
import CreateNewRequest from './pages/Requestor/CreateRequest/CreateNewRequest'
import RequestDetails from './pages/Requestor/CreateRequest/RequestDetails'
import CreateVacationBidRequest from './pages/Requestor/CreateRequest/CreateVacationBidRequest'
import VacationBidDetails from './pages/Requestor/CreateRequest/VacationBidDetails'
import EditSubmittedRequest from './pages/Requestor/StatusViews/EditSubmittedRequest'
import Login from './components/common/Auth/Login'
import AuthCallback from './components/common/Auth/CallBack'
import RoleProtectedRoute from './components/common/Auth/RoleProtectedRoute'
import AdminProxyRouteGuard from './components/common/Auth/AdminProxyRouteGuard'
import LandingPage from './pages/Admin/LandingPage'
import ManageProxy from './pages/Admin/ManageProxy/ManageProxy'
import ManualTimeOff from './pages/Admin/ManageProxy/components/ManualTimeOff'
import { useRoleGuard } from './hooks/useRoleGaurd'
import RequestProcessContainer from './pages/Approver/ProcessTimeOffRequest/RequestProcessContainer'
import ManualTimeOffContextGuard from './components/common/ManualTimeOffContextGuard'
import AnalyticsRoute from './components/common/AnalyticsRoute'
import ServiceUnavailable from './components/common/ServiceUnavailable'
import {
  TORT_TEMPORARILY_UNAVAILABLE_EVENT,
  type TortTemporarilyUnavailableDetail,
} from './utils/globalApiStatus'
import RequestorRouteContextGuard from './components/common/RequestorRouteContextGuard'
import RequestorApproverGuard from './components/common/RequestorApproverGuard'

/** Renders authenticated application routes and global availability state. */
function App() {
  const [overlayVisible, setOverlayVisible] = useState(false)
  const [isTortUnavailable, setIsTortUnavailable] = useState(false)
  const [tortUnavailableStatus, setTortUnavailableStatus] = useState<number | undefined>(undefined)

  // Get default route based on user roles
  const { isAdmin, isApprover, isApproverProxy } = useRoleGuard(['admin', 'approver', 'requestor'])
  
  let defaultRoute = '/requestor'
  if (isAdmin) {
    defaultRoute = '/admin'
  } else if (isApprover || isApproverProxy) {
    defaultRoute = '/approver'
  }

  // Listen for custom events to toggle overlay when modals open/close  
  useEffect(() => {
    const onOpen = () => setOverlayVisible(true)
    const onClose = () => setOverlayVisible(false)
    const onTortUnavailable = (event: Event) => {
      const detail = (event as CustomEvent<TortTemporarilyUnavailableDetail>).detail
      setTortUnavailableStatus(detail?.status)
      setIsTortUnavailable(true)
    }
    globalThis.addEventListener('app-modal-open', onOpen)
    globalThis.addEventListener('app-modal-close', onClose)
    globalThis.addEventListener(TORT_TEMPORARILY_UNAVAILABLE_EVENT, onTortUnavailable)
    return () => {
      globalThis.removeEventListener('app-modal-open', onOpen)
      globalThis.removeEventListener('app-modal-close', onClose)
      globalThis.removeEventListener(TORT_TEMPORARILY_UNAVAILABLE_EVENT, onTortUnavailable)
    }
  }, [])
  return (
    <Router>
      <ScrollToTop />
      <ManualTimeOffContextGuard />
      {/* App-level overlay to cover header + body when modal is open */}
      <div
        aria-hidden={!overlayVisible}
        className={`app-overlay ${
          overlayVisible ? 'app-overlay--visible' : ''
        }`}
      />
      <Routes>
        {/* Public auth routes - no Header/Footer */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Protected routes - all wrapped with ProtectedRoute */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <>
                <Header />
                {isTortUnavailable ? (
                  <ServiceUnavailable status={tortUnavailableStatus} />
                ) : (
                  <main>
                    <Routes>
                    {/* Redirect root to user's default role route */}
                    <Route path="/" element={<Navigate to={defaultRoute} replace />} />
                    
                    {/* Dynamic Requestor routes - works for both /requestor and /admin/requestor */}
                    <Route path="/:mode?/requestor" element={<AnalyticsRoute pageType="requestor-home"><TimeOffPage /></AnalyticsRoute>} />
                    <Route element={<RequestorApproverGuard />}>
                    <Route path="/:mode?/requestor/create" element={<AnalyticsRoute pageType="requestor-create"><CreateNewRequest /></AnalyticsRoute>} />
                    <Route path="/:mode?/requestor/request" element={<AnalyticsRoute pageType="requestor-request-confirmation"><RequestorRouteContextGuard kind="request"><RequestDetails /></RequestorRouteContextGuard></AnalyticsRoute>} />
                    <Route path="/:mode?/requestor/edit-submitted/:requestId" element={<AnalyticsRoute pageType="requestor-edit-submitted"><EditSubmittedRequest /></AnalyticsRoute>} />
                    <Route path="/:mode?/requestor/create-vacation-bid" element={<AnalyticsRoute pageType="requestor-create-vacation-bid"><CreateVacationBidRequest /></AnalyticsRoute>} />
                    <Route path="/:mode?/requestor/vacation-bid" element={<AnalyticsRoute pageType="requestor-vacation-bid"><RequestorRouteContextGuard kind="vacationBid"><VacationBidDetails /></RequestorRouteContextGuard></AnalyticsRoute>} />
                    <Route path="/:mode?/requestor/status/:id" element={<AnalyticsRoute pageType="requestor-status-detail"><RequestStatusSummary /></AnalyticsRoute>} />
                    </Route>

                    {/* Approver Routes */}
                    <Route
                      path="/approver"
                      element={
                        <RoleProtectedRoute allowedRoles={['approver']}>
                          <AnalyticsRoute pageType="approver-home">
                            <ApproverContainer />
                          </AnalyticsRoute>
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/approver/process/:requestId"
                      element={
                        <RoleProtectedRoute allowedRoles={['approver']}>
                          <AnalyticsRoute pageType="approver-process">
                            <RequestProcessContainer />
                          </AnalyticsRoute>
                        </RoleProtectedRoute>
                      }
                    />

                    {/* Admin Routes */}
                    <Route
                      path="/admin"
                      element={
                        <RoleProtectedRoute allowedRoles={['admin', 'apAdmin', 'feAdmin', 'rpAdmin']}>
                          <AnalyticsRoute pageType="admin-home">
                            <LandingPage />
                          </AnalyticsRoute>
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/manual-timeoff"
                      element={
                        <AnalyticsRoute pageType="admin-manual-timeoff">
                          <ManualTimeOff />
                        </AnalyticsRoute>
                      }
                    />
                    <Route
                      path="/admin/:type"
                      element={
                        <AdminProxyRouteGuard>
                          <AnalyticsRoute pageType="admin-manage-proxy">
                            <ManageProxy />
                          </AnalyticsRoute>
                        </AdminProxyRouteGuard>
                      }
                    />
                    
                    <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </main>
                )}
                {!isTortUnavailable && <Footer />}
              </>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  )
}

export default App
